import {
  ALPHANUMERIC_MAX_CHARS,
  TEXT_MAX_CHARS,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import yup from '@/libs/validation';

import { useTranslation } from '@/i18n';
import { useMemo } from 'react';
import { FieldValues, RegisterOptions } from 'react-hook-form';
import { ValidationError } from 'yup';

export type ValidationProperty = {
  maxChars?: number;
  minChars?: number;
  componentType: string;
};

export type ValidationMessageKeys = {
  textMinLength?: string;
  textMaxLength?: string;
  alphanumericMinLength?: string;
  alphanumericMaxLength?: string;
  alphanumericPattern?: string;
};

const DEFAULT_MESSAGE_KEYS: Required<ValidationMessageKeys> = {
  textMinLength: 'transaction_items.validation_message.text.length',
  textMaxLength: 'transaction_items.validation_message.text.length',
  alphanumericMinLength:
    'transaction_items.validation_message.alphanumeric.length',
  alphanumericMaxLength:
    'transaction_items.validation_message.alphanumeric.length',
  alphanumericPattern:
    'transaction_items.validation_message.alphanumeric.pattern',
};

type SchemaCreator = (
  property: ValidationProperty,
  keys: Required<ValidationMessageKeys>,
  t: (key: string, params?: Record<string, unknown>) => string
) => yup.StringSchema;

const createTextSchema: SchemaCreator = (property, keys, t) => {
  let schema = yup.string().optional();

  schema = schema.max(
    property.maxChars ?? TEXT_MAX_CHARS,
    t(keys.textMaxLength, {
      maxLength: property.maxChars ?? TEXT_MAX_CHARS,
      minLength: property.minChars ?? 0,
    })
  );

  schema = schema.min(
    property.minChars ?? 0,
    t(keys.textMinLength, {
      maxLength: property.maxChars ?? TEXT_MAX_CHARS,
      minLength: property.minChars ?? 0,
    })
  );

  return schema;
};

const createAlphanumericSchema: SchemaCreator = (property, keys, t) => {
  let schema = yup.string().optional();

  schema = schema.max(
    property.maxChars ?? ALPHANUMERIC_MAX_CHARS,
    t(keys.alphanumericMaxLength, {
      maxLength: property.maxChars ?? ALPHANUMERIC_MAX_CHARS,
      minLength: property.minChars ?? 0,
    })
  );

  schema = schema.min(
    property.minChars ?? 0,
    t(keys.alphanumericMinLength, {
      maxLength: property.maxChars ?? ALPHANUMERIC_MAX_CHARS,
      minLength: property.minChars ?? 0,
    })
  );

  schema = schema.matches(
    /^[a-zA-Z0-9\sＡ-Ｚａ-ｚ０-９]*$/,
    t(keys.alphanumericPattern)
  );

  return schema;
};

const COMPONENT_TYPE_SCHEMA_MAP: Record<string, SchemaCreator | undefined> = {
  text: createTextSchema,
  alphanumeric: createAlphanumericSchema,
};

export const createValidationYupSchema = (
  property: ValidationProperty,
  t: (key: string, params?: Record<string, unknown>) => string,
  messageKeys?: ValidationMessageKeys
): yup.StringSchema<string | null | undefined> => {
  const { componentType } = property;

  const keys = {
    ...DEFAULT_MESSAGE_KEYS,
    ...messageKeys,
  };

  const schemaCreator = COMPONENT_TYPE_SCHEMA_MAP[componentType];
  if (!schemaCreator) {
    return yup.string().nullable().optional();
  }

  return schemaCreator(property, keys, t);
};

export const useValidationRule = <
  TFieldValues extends FieldValues = FieldValues
>(
  property: ValidationProperty,
  messageKeys?: ValidationMessageKeys
): RegisterOptions<TFieldValues> => {
  const { t } = useTranslation();

  return useMemo(() => {
    const yupSchema = createValidationYupSchema(property, t, messageKeys);

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validate: async (value: any) => {
        try {
          await yupSchema.validate(value);
          return true;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (error instanceof ValidationError) {
            return error.message;
          }
          return error?.message || t('system_error_input');
        }
      },
    };
  }, [property, t, messageKeys]);
};
